// ActiveInstallationsMap.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
    GoogleMap,
    useLoadScript,
    OverlayView,
    InfoWindow,
} from "@react-google-maps/api";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { InstallationStatusBadge } from "components/Installationstatus";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { components } from "react-select";

// Installer option type
import { getAuthCookie } from "../utils/authCookies";

type InstallerOption = {
    value: string;
    label: string;
    color?: string;
};

// Page size for async paginate (dropdown)
const PAGE_SIZE = 10;
const ALL_INSTALLERS_OPTION: InstallerOption = {
    value: "all",
    label: "All",
    color: "#6366f1",
};
// Page size for Installers Details card (horizontal scroll)
const INSTALLERS_DETAILS_PAGE_SIZE = 12;

// Custom dropdown indicator component
const DropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }}></i>
        </components.DropdownIndicator>
    );
};
// Custom Option render to show assigned_color dot before label
const InstallerOption = (props: any) => {
    const color = props.data?.color;
    return (
        <components.Option {...props}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                    style={{
                        width: 12,
                        height: 12,
                        borderRadius: 999,
                        background: color || "#cbd5e1",
                        display: "inline-block",
                        flex: "0 0 auto",
                    }}
                />
                <div>{props.data.label}</div>
            </div>
        </components.Option>
    );
};

// Custom MultiValueLabel to show color dot inside selected chips
const InstallerMultiValueLabel = (props: any) => {
    const color = props.data?.color;
    return (
        <components.MultiValueLabel {...props}>
            <span
                style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: color || "#cbd5e1",
                    display: "inline-block",
                    marginRight: 6,
                }}
            />
            {props.data.label}
        </components.MultiValueLabel>
    );
};

type DateRangeType = "today" | "yesterday" | "last_30_days" | "next_15_days" | "next_7_days" | "custom";

type ActiveInstallationsMapProps = {
    title?: string;
    searchUrl: string; // POST
    detailsUrl: (id: string) => string; // GET
    viewOrderHref: (orderId: string) => string;
    requestRemovalHref?: (orderId: string) => string;
    initialZoom?: number;
};

const DEFAULT_CENTER = { lat: 33.7490, lng: -84.3880 };

// —— status lookups (labels, icon classes and colors) ———————————————
const STATUS_META: Record<
    number,
    {
        key: "finalized" | "scheduled" | "inprogress" | "completed";
        label: "Finalized" | "Scheduled" | "In Progress" | "Completed";
        icon: string;
        border: string;
        bg: string;
    }
> = {
    200: {
        key: "finalized",
        label: "Finalized",
        icon: "ri-error-warning-line",
        border: "#d08700",
        bg: "#f0b100",
    },
    300: {
        key: "scheduled",
        label: "Scheduled",
        icon: "ri-calendar-line",
        border: "#155dfc",
        bg: "#2b7fff",
    },
    400: {
        key: "inprogress",
        label: "In Progress",
        icon: "ri-time-line",
        border: "#f54a00",
        bg: "#ff6900",
    },
    500: {
        key: "completed",
        label: "Completed",
        icon: "ri-checkbox-circle-line",
        border: "#00a63e",
        bg: "#00c951",
    },
};

export default function ActiveInstallationsMap({
    title = "Active Installations Map",
    searchUrl,
    detailsUrl,
    viewOrderHref,
    requestRemovalHref,
    initialZoom = 9,
}: ActiveInstallationsMapProps) {
    const mapRef = React.useRef<google.maps.Map | null>(null);
    const token = getAuthCookie("sessionToken");

    const [searchInput, setSearchInput] = React.useState<string>("");
    const [statusFilter, setStatusFilter] = React.useState<string>("");
    const [priorityFilter, setPriorityFilter] = React.useState<string>("");
    const [dateRange, setDateRange] =
        React.useState<DateRangeType>("last_30_days");
    const [fromDate, setFromDate] = React.useState<string>("");
    const [toDate, setToDate] = React.useState<string>("");
    const [locations, setLocations] = React.useState<any[]>([]);
    const [selectedInstallation, setSelectedInstallation] =
        React.useState<any | null>(null);
    const [selectedCenter, setSelectedCenter] = React.useState<
        { lat: number; lng: number } | null
    >(null);
    const [productImages, setProductImages] = useState<any[]>([]);

    // Installers filter state (dropdown)
    const [selectedInstallers, setSelectedInstallers] = useState<InstallerOption[]>([]);

    // Installers Details state (card below map — separate from dropdown)
    const [installersDetailsList, setInstallersDetailsList] = useState<InstallerOption[]>([]);
    const [installersDetailsPage, setInstallersDetailsPage] = useState(1);
    const [installersDetailsSearch, setInstallersDetailsSearch] = useState("");
    const [installersDetailsHasMore, setInstallersDetailsHasMore] = useState(true);
    const [installersDetailsLoading, setInstallersDetailsLoading] = useState(false);
    const installersScrollRef = React.useRef<HTMLDivElement | null>(null);
    const installersDetailsLoadingRef = React.useRef(false);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    });

    // Shared fetch: /team-members/datatable — used by dropdown and Installers Details card (different state)
    const fetchTeamMembersPage = useCallback(
        async (page: number, search: string, limit: number) => {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/organization-memberships/team-members/datatable`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        page,
                        limit,
                        search: (search || "").trim(),
                        filters: { userType: "team_member" },
                        sort: { field: "firstName", order: "asc" },
                        fields: ["firstName", "lastName", "assigned_color"],
                    }),
                }
            );
            const json = await res.json();
            const teamMembers = json.data ?? [];
            const totalPages = json.totalPages ?? 1;
            const items: InstallerOption[] = teamMembers.map((member: any) => ({
                value: String(member?.id ?? member?._id ?? ""),
                label: `${member?.firstName || ""} ${member?.lastName || ""}`.trim() || "--",
                color: member?.assigned_color || undefined,
            }));
            return { items, totalPages, ok: res.ok };
        },
        [token]
    );

    // Load installer options (dropdown) — uses shared fetch, separate state from Installers Details
    const loadInstallerOptions: LoadOptions<InstallerOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const currentPage = additional?.page ?? 1;
            const searchQuery = (inputValue || "").trim();
            try {
                const { items, totalPages, ok } = await fetchTeamMembersPage(currentPage, searchQuery, PAGE_SIZE);
                if (!ok) return { options: [], hasMore: false, additional: { page: currentPage } };
                return {
                    options: currentPage === 1 ? [ALL_INSTALLERS_OPTION, ...items] : items,
                    hasMore: currentPage < totalPages,
                    additional: { page: currentPage + 1 },
                };
            } catch (err) {
                console.error("Failed to load installer options:", err);
                return { options: [], hasMore: false, additional: { page: currentPage } };
            }
        },
        [fetchTeamMembersPage]
    );

    // Load Installers Details (card below map) — same API, own state; append=false for search/reset, true for scroll
    const fetchInstallersDetails = useCallback(
        async (page: number, search: string, append: boolean) => {
            if (installersDetailsLoadingRef.current) return;
            installersDetailsLoadingRef.current = true;
            setInstallersDetailsLoading(true);
            try {
                const { items, totalPages } = await fetchTeamMembersPage(
                    page,
                    search,
                    INSTALLERS_DETAILS_PAGE_SIZE
                );
                setInstallersDetailsHasMore(page < totalPages);
                setInstallersDetailsList((prev) => (append ? [...prev, ...items] : items));
            } catch (err) {
                console.error("Failed to load installers details:", err);
                setInstallersDetailsHasMore(false);
            } finally {
                installersDetailsLoadingRef.current = false;
                setInstallersDetailsLoading(false);
            }
        },
        [fetchTeamMembersPage]
    );

    // Initial load of Installers Details (card below map)
    useEffect(() => {
        setInstallersDetailsPage(1);
        fetchInstallersDetails(1, "", false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-load next pages until list fills container width (like dropdown filling height)
    useEffect(() => {
        if (installersDetailsLoading || !installersDetailsHasMore) return;
        const el = installersScrollRef.current;
        if (!el) return;
        const tryFill = () => {
            if (installersDetailsLoadingRef.current || !installersDetailsHasMore) return;
            if (el.scrollWidth <= el.clientWidth) {
                setInstallersDetailsPage((p) => {
                    const next = p + 1;
                    fetchInstallersDetails(next, installersDetailsSearch, true);
                    return next;
                });
            }
        };
        const id = requestAnimationFrame(() => requestAnimationFrame(tryFill));
        return () => cancelAnimationFrame(id);
    }, [installersDetailsList.length, installersDetailsLoading, installersDetailsHasMore, installersDetailsSearch, fetchInstallersDetails]);

    // Installers Details search: call API only on Enter
    const handleInstallersSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                setInstallersDetailsPage(1);
                fetchInstallersDetails(1, installersDetailsSearch, false);
            }
        },
        [installersDetailsSearch, fetchInstallersDetails]
    );

    // Load more when user scrolls to end (X direction)
    const handleInstallersScroll = useCallback(() => {
        const el = installersScrollRef.current;
        if (!el || installersDetailsLoadingRef.current || !installersDetailsHasMore) return;
        const { scrollLeft, clientWidth, scrollWidth } = el;
        if (scrollWidth > clientWidth && scrollLeft + clientWidth >= scrollWidth - 50) {
            setInstallersDetailsPage((p) => {
                const next = p + 1;
                fetchInstallersDetails(next, installersDetailsSearch, true);
                return next;
            });
        }
    }, [installersDetailsSearch, installersDetailsHasMore, fetchInstallersDetails]);

    // —— helpers ——————————————————————————————————————————————
    const roundCoord = (num: number) => Number(num?.toFixed(5));
    const getOffsetPosition = (
        lat: number,
        lng: number,
        index: number,
        offset = 0.00005
    ) => {
        const angle = (index * 45) * (Math.PI / 180);
        return {
            lat: lat + offset * Math.cos(angle),
            lng: lng + offset * Math.sin(angle),
        };
    };

    const locationGroups = React.useMemo(() => {
        return locations.reduce((acc, loc) => {
            const a = loc?.address?.address_details || loc?.installation_address?.address_details;
            const lat = roundCoord(a?.latitude ?? 0);
            const lng = roundCoord(a?.longitude ?? 0);
            const key = `${lat},${lng}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(loc);
            return acc;
        }, {} as Record<string, any[]>);
    }, [locations]);

    // —— fetch list —————————————————————————————————————————————
    const handleSearch = React.useCallback(async () => {
        // Get selected installer IDs
        const allInstallers = selectedInstallers.some(i => i.value === "all");
        const installerIds = allInstallers ? [] : selectedInstallers.map(i => i.value);

        const body =
            dateRange === "custom"
                ? {
                    search: searchInput,
                    status: statusFilter ? parseInt(statusFilter) : null,
                    priority: priorityFilter ? parseInt(priorityFilter) : null,
                    date_range: { type: "custom", from: fromDate, to: toDate },
                    all_installers: allInstallers,
                    installer_ids: installerIds,
                }
                : {
                    search: searchInput,
                    status: statusFilter ? parseInt(statusFilter) : null,
                    priority: priorityFilter ? parseInt(priorityFilter) : null,
                    date_range: { type: dateRange, from: "", to: "" },
                    all_installers: allInstallers,
                    installer_ids: installerIds,
                };

        await fetchWithBody(body);
    }, [searchUrl, token, searchInput, statusFilter, priorityFilter, dateRange, fromDate, toDate, selectedInstallers]);

    React.useEffect(() => {
        handleSearch();
    }, []); // initial
    

    // —— fetch details for a single marker ————————————————————
    const fetchDetails = React.useCallback(
        async (locationId: string) => {
            try {
                const res = await fetch(detailsUrl(locationId), {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                const data = await res.json();
                if (!res.ok)
                    throw new Error(data.message || "Failed to fetch location details");
                return data;
            } catch (err: any) {
                console.error(err);
                toast.error("Failed to fetch installation location.");
                return null;
            }
        },
        [detailsUrl, token]
    );

    const formatDate = (iso?: string) =>
        iso ? new Date(iso).toDateString() : "-";

    // —— overlay offset so the “pin” bottom sits on the LatLng ——
    const overlayOffset = React.useCallback(
        (width: number, height: number) => ({ x: -width / 2, y: -height }),
        []
    );

    const statusMeta = (s?: number) => {
        switch (s) {
            case 200: return { label: "Finalized", bg: "#f0b100", border: "#d08700" };
            case 300: return { label: "Scheduled", bg: "#2b7fff", border: "#155dfc" };
            case 400: return { label: "In Progress", bg: "#ff6900", border: "#f54a00" };
            case 500: return { label: "Completed", bg: "#00c951", border: "#00a63e" };
            default: return { label: "Unknown", bg: "#6b7280", border: "#9ca3af" };
        }
    };

    const fmtMaybeMongoDate = (d?: any) => {
        const raw = d?.$date ?? d;
        if (!raw) return "-";
        const dt = new Date(raw);
        return isNaN(dt.getTime())
            ? "-"
            : dt.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "2-digit" });
    };

    const fullName = (x: any) => {
        const first = x?.first_name || x?.address?.first_name || "";
        const last = x?.last_name || x?.address?.last_name || "";
        const name = `${first} ${last}`.trim();
        return name || x?.agent_name || "-";
    };

    const displayAddress = (x: any) => {
        const ad = x?.address?.address_details || x?.address;
        if (ad?.label) return ad.label;
        const parts = [ad?.addressLine1, ad?.addressLine2, ad?.city, ad?.state, ad?.zip].filter(Boolean);
        return parts.length ? parts.join(", ") : "-";
    };

    // normalize relative file paths like "uploads\\...jpg"
    const resolveFileUrl = (u?: string) => {
        if (!u) return "";
        const normalized = u.replace(/\\/g, "/");
        if (/^https?:\/\//i.test(normalized)) return normalized;
        const base = process.env.REACT_APP_BASE_URL;
        return `${base}${normalized.replace(/^\/+/, "")}`;
    };

    // Clear all filters and search
    const handleClear = () => {
        setSearchInput("");
        setStatusFilter("");
        setPriorityFilter("");
        setDateRange("last_30_days");
        setFromDate("");
        setToDate("");
        setSelectedInstallation(null);
        setSelectedInstallers([]);

        // Call handleSearch with cleared values immediately
        const clearedBody = {
            search: "",
            status: null,
            priority: null,
            date_range: { type: "last_30_days", from: "", to: "" },
            all_installers: false,
            installer_ids: [],
        };

        // Perform the search with cleared values
        fetchWithBody(clearedBody);
    };

    // Helper function to perform search with specific body
    const fetchWithBody = React.useCallback(async (body: any) => {
        setSelectedInstallation(null);
        try {
            const res = await fetch(searchUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.message || "Failed to fetch map data");

            setLocations(result.locations || []);
        } catch (err: any) {
            console.error(err);
            toast.error("Failed to load map data.");
        }
    }, [searchUrl, token]);

    const idOf = (x?: any): string => (typeof x === "string" ? x : (x?.url ?? "")) || "";
    const isCloudFileId = (s?: string) => !!s && /^[0-9a-f]{24}$/i.test(s);
    const buildImageUrl = (raw?: string) => {
        if (!raw) return "";
        if (/^https?:\/\//i.test(raw)) return raw;
        const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
        const path = String(raw).replace(/\\/g, "/").replace(/^\/+/, "");
        return `${base}/${encodeURI(path)}`;
    };

    const productImageIds = React.useMemo(
        () => (productImages || []).map((img: any) => idOf(img)).filter(Boolean),
        [productImages]
    );

    const dataForMap = React.useMemo(() => {
        if (!productImageIds.length) return [];
        return [{ product: { variants: [{ images: productImageIds.map((url) => ({ url })) }] } }];
    }, [productImageIds]);

    const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

    const resolvedImages = React.useMemo(
        () =>
            (productImages || []).map((img: any) => {
                const id = idOf(img);
                const presigned = getImageSrc(id, imageUrlMap);
                // Cloud file ids must wait for presign — never build a legacy URL from the id alone.
                const src = presigned || (isCloudFileId(id) ? "" : buildImageUrl(id));
                return {
                    src,
                    alt: img?.alt_text || "",
                };
            }),
        [productImages, imageUrlMap]
    );
    // console.log("Resolved imagesActive:", resolvedImages);

    return (
        <div className="content">
            {/* Top Row: Title and Search */}
            <div className="row align-items-center g-3 mb-3">
                <div className="col-lg-6">
                    <h3 className="m-0 fw-bold">{title}</h3>
                </div>
                <div className="col-lg-3 offset-lg-3">
                    <div className="input-group">
                        <span className="input-group-text bg-white border-end-0 p-2 left-radius">
                            <i className="ri-search-line fs-15"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control border-start-0 ps-0 right-radius text-ellipsis"
                            placeholder="Search Installation ID, Order #, Address..."
                            value={searchInput}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === "Enter") {
                                    handleSearch();
                                }
                            }}
                            style={{ borderColor: "#cccccc" }}
                        />
                    </div>
                </div>
            </div>

            <hr className="text-secondary" />

            {/* Bottom Row: Filters and Buttons */}
            <div className="align-items-end g-3 justify-content-lg-end mb-3 row">
                <div className="col-lg-1">
                    <label className="form-label mb-1">Status</label>
                    <select
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        <option value="200">Finalized</option>
                        <option value="300">Scheduled</option>
                        <option value="400">In Progress</option>
                        <option value="500">Completed</option>
                    </select>
                </div>

                <div className="col-lg-1">
                    <label className="form-label mb-1">Date Range</label>
                    <select
                        className="form-select"
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value as DateRangeType)}
                    >
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        {/* <option value="last_15_days">Last 15 Days</option> */}
                        <option value="last_30_days">Last 30 Days</option>
                        <option value="next_15_days">Next 15 Days</option>
                        <option value="next_7_days">Next 7 Days</option>
                        <option value="custom">Date Range</option>
                    </select>
                </div>
                {dateRange === "custom" && (
                    <>
                        <div className="col-lg-1">
                            <label className="form-label mb-1">From</label>
                            <input
                                type="date"
                                className="form-control"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className="col-lg-1">
                            <label className="form-label mb-1">To</label>
                            <input
                                type="date"
                                className="form-control"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                    </>
                )}

                <div className="col-lg-1">
                    <label className="form-label mb-1">Priority</label>
                    <select
                        className="form-select"
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                    >
                        <option value="">All Priorities</option>
                        <option value="1">Critical</option>
                        <option value="2">High Priority</option>
                        <option value="3">Normal</option>
                    </select>
                </div>

                <div className="col-lg-2">
                    <label className="form-label mb-1">Installers</label>
                    <AsyncPaginate
                        isMulti
                        value={selectedInstallers}
                        loadOptions={loadInstallerOptions}
                        onChange={(selected) => {
                            const nextSelected = selected ? [...selected] : [];
                            setSelectedInstallers(
                                nextSelected.some((item) => item.value === "all")
                                    ? [ALL_INSTALLERS_OPTION]
                                    : nextSelected
                            );
                        }}
                        additional={{ page: 1 }}
                        defaultOptions
                        debounceTimeout={300}
                        placeholder="Installers"
                        classNamePrefix="rs"
                        components={{ DropdownIndicator, Option: InstallerOption, MultiValueLabel: InstallerMultiValueLabel }}
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "38px",
                                borderColor: "#dee2e6",
                                "&:hover": { borderColor: "#86b7fe" },
                            }),
                            dropdownIndicator: (base) => ({
                                ...base,
                                padding: "8px",
                            }),
                            multiValue: (base) => ({
                                ...base,
                                backgroundColor: "#e7f1ff",
                            }),
                            multiValueLabel: (base) => ({
                                ...base,
                                color: "#0d6efd",
                            }),
                            multiValueRemove: (base) => ({
                                ...base,
                                color: "#0d6efd",
                                "&:hover": {
                                    backgroundColor: "#0d6efd",
                                    color: "#fff",
                                },
                            }),
                        }}
                    />
                </div>

                <div className="col-lg-1">
                    <button className="btn btn-primary table-addbutton br-8 w-100" onClick={handleSearch}>
                        <i className="ri-filter-3-line me-2"></i>
                        Apply Filter
                    </button>
                </div>

                <div className="col-lg-1">
                    <button className="btn back-btn surface-btn fw-bold fs-16 w-100 br-8" onClick={handleClear}>
                        Clear
                    </button>
                </div>
            </div>

            <div className="row g-3">
                {/* MAP */}
                <div className="col-lg-9 mb-4 mb-lg-0">
                    <div className="bg-white box-shadow overflow-hidden position-relative rounded-4 shadow-border-3">
                        {isLoaded && (
                            <GoogleMap
                                mapContainerStyle={{ width: "100%", height: 560 }}
                                zoom={initialZoom}
                                center={selectedCenter || DEFAULT_CENTER}
                                onLoad={(map) => {
                                    mapRef.current = map;
                                }}
                            >
                                {/* custom overlay markers */}
                                {locations.map((loc, idx) => {
                                    const a =
                                        loc.address?.address_details ||
                                        loc.installation_address?.address_details;
                                    const lat = roundCoord(a?.latitude);
                                    const lng = roundCoord(a?.longitude);
                                    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;

                                    const groupLen =
                                        (locationGroups[`${lat},${lng}`]?.length || 1) as number;
                                    const title =
                                        groupLen > 1
                                            ? `📦 ${groupLen} installations here — zoom in`
                                            : `Order: ${loc.order_number}`;

                                    // determine pin appearance:
                                    // - if status is 500 (completed) use STATUS_META color/icon
                                    // - otherwise use assigned_color from API (fallback gray)
                                    const meta = STATUS_META[loc.status as number];
                                    const isCompleted = loc.status === 500;
                                    const assignedColor = loc.assigned_color || "#9ca3af";

                                    const pinBg = isCompleted ? (meta?.bg || "#00c951") : assignedColor;
                                    const pinBorder = isCompleted ? (meta?.border || "#00a63e") : assignedColor;

                                    const pos = getOffsetPosition(lat, lng, idx);

                                    return (
                                        <OverlayView
                                            key={`${loc._id}-${idx}`}
                                            position={pos}
                                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                            getPixelPositionOffset={overlayOffset}
                                        >
                                            <button
                                                type="button"
                                                className="gm-pin"
                                                title={title}
                                                onClick={async () => {
                                                    const details = await fetchDetails(loc._id);
                                                    if (!details) return;
                                                    setSelectedInstallation(details);
                                                    // ✅ store the entire proof array in a consistent shape
                                                    const proofs = Array.isArray(details.proof_of_installation)
                                                        ? details.proof_of_installation
                                                        : [];

                                                    setProductImages(
                                                        proofs.map((p: any) => ({
                                                            url: p?.file_url ?? "",
                                                            alt_text: p?.file_name ?? "",
                                                        }))
                                                    );

                                                    setSelectedCenter({ lat, lng });
                                                }}
                                                // set CSS vars so pin background and border use chosen colors
                                                style={
                                                    {
                                                        ['--pin-bg' as any]: pinBg,
                                                        ['--pin-border' as any]: pinBorder,
                                                    } as React.CSSProperties
                                                }
                                            >
                                                <span className="gm-pin__wave" />
                                                <span className="gm-pin__dot">
                                                    {isCompleted && meta?.icon ? <i className={meta.icon} /> : null}
                                                </span>
                                            </button>
                                        </OverlayView>
                                    );
                                })}

                                {/* Custom POPUP (OverlayView) — replaces InfoWindow */}
                                {selectedInstallation && selectedCenter && (() => {
                                    const meta =
                                        STATUS_META[selectedInstallation.status as number] ||
                                        STATUS_META[300];
                                    const orderId =
                                        selectedInstallation?.order_id ||
                                        selectedInstallation?.installation_order_id ||
                                        selectedInstallation?._id;

                                    const prettyDate = (iso?: string) =>
                                        iso ? new Date(iso).toLocaleDateString(undefined, {
                                            weekday: "short",
                                            year: "numeric",
                                            month: "short",
                                            day: "2-digit",
                                        }) : "-";

                                    const addr =
                                        selectedInstallation?.address?.label ||
                                        `${selectedInstallation?.address?.addressLine1 || ""} ${selectedInstallation?.address?.city || ""}, ${selectedInstallation?.address?.state || ""}`.trim();

                                    return (
                                        <OverlayView
                                            position={selectedCenter}
                                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                        >
                                            <div
                                                className="map-pop"
                                                style={{ transform: "translate(-50%, calc(-100% - 14px))" }}
                                            >
                                                {/* Header */}
                                                <div className="header">
                                                    <span
                                                        className="chip"
                                                        style={{
                                                            background: meta.bg,
                                                            border: `1px solid ${meta.border}`,
                                                            color: "#fff",
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: 999,
                                                            display: "grid",
                                                            placeItems: "center",
                                                            fontSize: 12,
                                                            fontWeight: 800,
                                                        }}
                                                        title={meta.label}
                                                    >
                                                        <i className={meta.icon} />
                                                    </span>

                                                    <div>
                                                        <div className="title">Order: {selectedInstallation?.order_number}</div>
                                                        <div className="sub">{prettyDate(selectedInstallation?.scheduled_date)}</div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="close"
                                                        onClick={() => setSelectedInstallation(null)}
                                                        aria-label="Close"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>

                                                {/* Status */}
                                                <div className="row mb-3" style={{ marginTop: 6 }}>
                                                    <div className="label">Status:</div>
                                                    <div className="text-end">
                                                        <span
                                                            className="badge"
                                                            style={{
                                                                color: "#fff",
                                                                background: meta.bg,
                                                                border: `1px solid ${meta.border}`,
                                                            }}
                                                        >
                                                            {meta.label}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Customer */}
                                                <div className="row mb-3">
                                                    <div className="label">Customer:</div>
                                                    <div className="value text-end">
                                                        {selectedInstallation?.agent_name ||
                                                            selectedInstallation?.brokerage_firm_name ||
                                                            selectedInstallation?.office?.officeName || "-"}
                                                    </div>
                                                </div>

                                                {/* Brokerage (if any) */}
                                                {(
                                                    <div className="row mb-3">
                                                        <div className="label">Brokerage:</div>
                                                        <div className="value text-end">{selectedInstallation.brokerage_firm_name ?? "-"}</div>
                                                    </div>
                                                )}

                                                <div className="divider" />

                                                {/* Address */}
                                                <div className="addr" title={addr}>
                                                    <i className="ri-map-pin-2-line" style={{ fontSize: 16, color: "#374151" }} />
                                                    <span>{addr}</span>
                                                </div>
                                            </div>
                                        </OverlayView>
                                    );
                                })()}

                            </GoogleMap>
                        )}

                        {/* LEGEND (bottom-left) */}
                        <div className="map-legend card shadow-sm border-0">
                            <div className="card-body py-2">
                                <div className="fw-semibold small mb-1">Status</div>
                                <ul className="list-unstyled d-grid gap-1 m-0 small">
                                    {/* <li className="d-flex align-items-center gap-2">
                                        <span className="legend-dot legend-dot--finalized" />
                                        <i className="ri-error-warning-line legend-ico" />
                                        <span className="fs-13">Finalized</span>
                                    </li>
                                    <li className="d-flex align-items-center gap-2">
                                        <span className="legend-dot legend-dot--scheduled" />
                                        <i className="ri-calendar-line legend-ico" />
                                        <span className="fs-13">Scheduled</span>
                                    </li>
                                    <li className="d-flex align-items-center gap-2">
                                        <span className="legend-dot legend-dot--inprogress" />
                                        <i className="ri-time-line legend-ico" />
                                        <span className="fs-13">In Progress</span>
                                    </li> */}
                                    <li className="d-flex align-items-center gap-2">
                                        <span className="legend-pin legend-pin--completed" title="Completed location">
                                            <i className={STATUS_META[500].icon} />
                                        </span>
                                        <span className="fs-15">Completed</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                {/* DETAILS SIDE PANEL (kept) */}
                <div className="box-shadow rounded-4 col-lg-3 shadow-border-3 mb-3 mb-lg-0">
                    <div className="bg-white p-3 rounded-4 h-100 details-card">
                        <h3 className="border-bottom fw-bold pb-4">Installation Details</h3>

                        {selectedInstallation ? (() => {
                            const meta = statusMeta(selectedInstallation?.status);
                            const orderId =
                                selectedInstallation?.order_id ||
                                selectedInstallation?.installation_order_id ||
                                selectedInstallation?._id;

                            return (
                                <>
                                    <div className="my-3">
                                        <div className="k">Order:</div>
                                        <a
                                            href={viewOrderHref(orderId)}
                                            className="fw-bold text-decoration-none text-secondary-emphasis v"
                                            target="_self"
                                            rel="noreferrer"
                                        >
                                            {selectedInstallation?.order_number || "-"} <i className="ri-external-link-line ms-1" />
                                        </a>
                                    </div>

                                    <div className="mb-3">
                                        <div className="k">Brokerage Firm:</div>
                                        <div className="v">{selectedInstallation?.brokerage_firm_name || "-"}</div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="k">Full Name:</div>
                                        <div className="v">{fullName(selectedInstallation)}</div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="k">Status:</div>
                                        <span className="pill" style={{ background: meta.bg, borderColor: meta.border }}>
                                            {meta.label}
                                        </span>
                                    </div>

                                    <div className="mb-3">
                                        <div className="k">Scheduled Date:</div>
                                        <div className="v">{fmtMaybeMongoDate(selectedInstallation?.scheduled_date)}</div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="k">Address:</div>
                                        <div className="v">{displayAddress(selectedInstallation)}</div>
                                    </div>

                                    <hr className="my-3" />
                                    <div className="mb-2">
                                        <div className="k">Proof of Installation:</div>

                                        {resolvedImages.some((img) => img.src) ? (
                                            <div className="proof-grid mt-2">
                                                {resolvedImages.filter((img) => img.src).map((img, i) => (
                                                    <a
                                                        key={i}
                                                        href={img.src}
                                                        className="proof-thumb"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title={img.alt || `Proof ${i + 1}`}
                                                    >
                                                        <img src={img.src} alt={img.alt || `proof-${i + 1}`} />
                                                    </a>
                                                ))}
                                            </div>
                                        ) : resolvedImages.length > 0 ? (
                                            <div className="text-muted small">Loading images...</div>
                                        ) : (
                                            <div className="text-muted small">No files uploaded.</div>
                                        )}
                                    </div>
                                    {/* <div className="mb-2">
                                        <div className="k">Proof of Installation:</div>
                                        {Array.isArray(selectedInstallation?.proof_of_installation) && selectedInstallation.proof_of_installation.length > 0 ? (
                                            <div className="proof-grid mt-2">
                                                {selectedInstallation.proof_of_installation.map((f: any, i: number) => {
                                                    const url = resolveFileUrl(f?.file_url);
                                                    return (
                                                        <a key={i} href={url} className="proof-thumb" target="_blank" rel="noreferrer" title={f?.file_name || "Proof"}>
                                                            <img src={url} alt={f?.file_name || `proof-${i + 1}`} />
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-muted small">No files uploaded.</div>
                                        )}
                                    </div> */}

                                    <div className="d-grid gap-2 mt-3">
                                        <a href={viewOrderHref(orderId)} className="btn btn-primary fw-bold">View Order</a>
                                        {requestRemovalHref && (
                                            <a href={requestRemovalHref(orderId)} className="btn btn-outline-danger fw-bold">Request Removal</a>
                                        )}
                                    </div>
                                </>
                            );
                        })() : (
                            <div className="text-muted">Click a pin to see details here.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Installers card below map — same /team-members/datatable, own state; horizontal scroll, load more on scroll */}
            <div className="row mt-4 ms-0">
                <div className="box-shadow col-lg-4 mb-3 mb-lg-0 rounded-4 shadow-border-3">
                    <div className="border-0 installers-details-card overflow-hidden py-4">
                        <div className="bg-white border-bottom py-2 px-3 d-flex align-items-center justify-content-between">
                            <h5 className="mb-0 fw-bold">Installers</h5>
                            <div className="input-group installers-details-search" style={{ maxWidth: 220 }}>
                                <span className="input-group-text bg-white border-end-0 py-1 px-2">
                                    <i className="ri-search-line text-muted" style={{ fontSize: 14 }} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0 py-1 ps-1"
                                    placeholder="Search installer... (press Enter)"
                                    value={installersDetailsSearch}
                                    onChange={(e) => setInstallersDetailsSearch(e.target.value)}
                                    onKeyDown={handleInstallersSearchKeyDown}
                                    style={{ fontSize: 13 }}
                                />
                            </div>
                        </div>
                        <div
                            ref={installersScrollRef}
                            className="py-2 px-3 installers-details-scroll"
                            onScroll={handleInstallersScroll}
                        >
                            <div className="installers-details-list">
                                <div className="installer-detail-item" key="unassigned">
                                    <span
                                        className="installer-detail-dot"
                                        style={{ background: "#9ca3af" }}
                                    />
                                    <span className="installer-detail-name fs-16">Unassigned</span>
                                </div>
                                {installersDetailsList.map((inst) => (
                                    <div
                                        key={inst.value}
                                        className="installer-detail-item"
                                    >
                                        <span
                                            className="installer-detail-dot"
                                            style={{ background: inst.color || "#cbd5e1" }}
                                        />
                                        <span className="installer-detail-name fs-16">{inst.label}</span>
                                    </div>
                                ))}
                                {installersDetailsLoading && (
                                    <div className="installer-detail-item text-muted" style={{ minWidth: 80 }}>
                                        <i className="ri-loader-4-line anim-spin" /> Loading…
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* component-scoped CSS for pins/legend/infowindow */}
            <style>{`
/* ——— Map legend ——— */
.map-legend {
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 5;
  border-radius: 12px;
}
.legend-dot {
  width: 12px; height: 12px; border-radius: 50%;
  display: inline-block; border: 2px solid var(--legend-border);
  background: var(--legend-bg);
}
.legend-ico { font-size: 14px; color: #6b7280; }
.legend-dot--finalized { --legend-border:#d08700; --legend-bg:#f0b100; }
.legend-dot--scheduled { --legend-border:#155dfc; --legend-bg:#2b7fff; }
.legend-dot--inprogress{ --legend-border:#f54a00; --legend-bg:#ff6900; }
.legend-dot--completed { --legend-border:#00a63e; --legend-bg:#00c951; }
/* legend pin: same as map location pointer for Completed (green circle + icon) */
.legend-pin {
  width: 26px; height: 26px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.legend-pin i { font-size: 23px; color: #fff; line-height: 1; }
.legend-pin--completed {
  background: #00c951;
  border: 2px solid #00a63e;
  box-shadow: 0 0 0 1px #00a63e;
}
.fs-13{ font-size:13px; }

/* ——— Custom map pin ——— */
.gm-pin {
  all: unset;
  position: relative;
  cursor: pointer;
  display: inline-block;
  width: 32px; height: 32px;
  transform: translateY(-4px);
  transition: transform .18s ease;
  will-change: transform;
}
.gm-pin:hover { transform: translateY(-4px) scale(1.06); }

/* inner dot that contains the icon */
.gm-pin__dot {
  width: 28px; height: 28px; border-radius: 50%;
  display: grid; place-items: center;
  box-shadow: 0 0 0 3px var(--pin-border);
  background: var(--pin-bg);
  border: 2px solid #fff;   /* thin white edge like Google pins */
}
.gm-pin i { font-size: 16px; color: #fff; line-height: 1; }

/* ripple "wave" every 3s */
.gm-pin__wave {
  position: absolute; inset: 0;
  border-radius: 50%;
  background: var(--pin-bg);
  opacity: .28;
  animation: gm-ripple 3s ease-out infinite;
}
@keyframes gm-ripple {
  0%   { transform: scale(1);   opacity: .28; }
  70%  { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(2.2); opacity: 0; }
}

/* status color vars for pins + info chips */
.gm-pin--finalized { --pin-border:#d08700; --pin-bg:#f0b100; }
.gm-pin--scheduled { --pin-border:#155dfc; --pin-bg:#2b7fff; }
.gm-pin--inprogress{ --pin-border:#f54a00; --pin-bg:#ff6900; }
.gm-pin--completed { --pin-border:#00a63e; --pin-bg:#00c951; }

/* InfoWindow card + status chip */
.poi-card { min-width: 320px; max-width: 360px; }
.status-chip {
  padding: 2px 8px; border-radius: 999px;
  font-size: 12px; color: #fff; font-weight: 600; white-space: nowrap;
}
.chip--finalized { background: #f0b100; }
.chip--scheduled { background: #2b7fff; }
.chip--inprogress{ background: #ff6900; }
.chip--completed { background: #00c951; }
/* —— Custom popup card —— */
.map-pop {
  width: 300px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,.12), 0 2px 6px rgba(0,0,0,.08);
  padding: 14px;
  border: 1px solid rgba(0,0,0,.06);
  font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial;
}
.map-pop .header { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.map-pop .title { font-weight: 700; color: #111827; font-size: 14px; line-height: 1.1; margin-bottom: 5px; }
.map-pop .sub { color: #6b7280; font-size: 13px; }
.map-pop .close {
  margin-left: auto; cursor: pointer; color: #6b7280; border-radius: 6px; padding: 4px; background: transparent; border: 0;
}
.map-pop .close:hover { background:#f3f4f6; color:#111827; }

.map-pop .row { display: grid; grid-template-columns: 110px 1fr; gap: 10px; align-items: center; font-size: 14px; margin: 6px 0; }
.map-pop .label { color: #6b7280; }
.map-pop .value { color: #111827; font-weight: 600; }
.map-pop .divider { height: 1px; background: #e5e7eb; margin: 8px 0; }

.map-pop .badge {
  display: inline-block; padding: 3px 10px; border-radius: 8px;
  font-size: 10px; font-weight: 700; line-height: 18px;
}
.map-pop .addr {
  display: flex; align-items: center; gap: 8px; color: #111827;
  font-size: 11px;
}

.details-card .k { color:black; font-size:12px; margin-bottom:4px; font-weight:600; }
.details-card .v { color:#111827;}
.details-card .pill {
  display:inline-block; padding:2px 7px; border-radius:8px; color:#fff;
  font-size:11px; font-weight:700; line-height:18px; border:1px solid transparent;
}
.proof-grid { display:flex; gap:12px; flex-wrap:wrap; }
.proof-thumb {
  width:110px; height:80px; border-radius:10px; overflow:hidden;
  border:1px solid #e5e7eb; background:#f8fafc; cursor:pointer;
}
.proof-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
button:hover{background:transparent;}

/* ——— Installers Details card (below map) ——— */
.installers-details-card { border-radius: 12px; }
.installers-details-scroll {
  overflow-x: scroll;
  overflow-y: hidden;
  height: 68px;
  max-width: 100%;
}
.installers-details-scroll::-webkit-scrollbar { height: 10px; }
.installers-details-scroll::-webkit-scrollbar-track { background:rgb(229, 235, 240); border-radius: 3px; }
.installers-details-scroll::-webkit-scrollbar-thumb { background:rgb(151, 150, 150); border-radius: 3px; cursor: pointer;}
.installers-details-list {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  min-width: min-content;
  padding-bottom: 2px;
}
.installer-detail-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  color: #334155;
}
.installer-detail-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.installer-detail-name { white-space: nowrap; }
.anim-spin { animation: installers-spin 0.8s linear infinite; }
@keyframes installers-spin { to { transform: rotate(360deg); } }

      `}</style>
        </div>
    );
}
